import type { LucideIcon } from 'lucide-react'
import {
	AudioLines,
	File,
	FileImage,
	FileSpreadsheet,
	FileText,
	FileType,
	FileVideoCamera,
	Presentation,
	Workflow,
} from 'lucide-react'
import type { PreviewCategory } from '@/types/artifacts'
import { getArtifactPreviewCategory } from '@/types/artifacts'

const EXTENSION_TO_CATEGORY: Record<string, PreviewCategory> = {
	pdf: 'pdf',
	mp3: 'audio',
	wav: 'audio',
	m4a: 'audio',
	ogg: 'audio',
	flac: 'audio',
	mp4: 'video',
	webm: 'video',
	mov: 'video',
	avi: 'video',
	png: 'image',
	jpg: 'image',
	jpeg: 'image',
	gif: 'image',
	webp: 'image',
	svg: 'image',
	heic: 'image',
	heif: 'image',
	txt: 'text',
	doc: 'word',
	docx: 'word',
	xls: 'excel',
	xlsx: 'excel',
	ppt: 'powerpoint',
	pptx: 'powerpoint',
	vsdx: 'visio',
	json: 'text',
	xml: 'text',
	sql: 'text',
	bpmn: 'bpmn',
}

function getCategoryFromExtension(extension: string): PreviewCategory {
	const ext = extension.toLowerCase().replace(/^\./, '')
	return EXTENSION_TO_CATEGORY[ext] ?? 'unsupported'
}

function getIconForCategory(category: PreviewCategory): LucideIcon {
	switch (category) {
		case 'audio':
			return AudioLines
		case 'image':
			return FileImage
		case 'video':
			return FileVideoCamera
		case 'pdf':
			return FileType
		case 'excel':
			return FileSpreadsheet
		case 'powerpoint':
			return Presentation
		case 'visio':
			return Workflow
		case 'word':
		case 'text':
			return FileText
		case 'bpmn':
			return Workflow
		default:
			return File
	}
}

export function getFileIcon(type: string) {
	const lowerType = type.toLowerCase()
	if (lowerType === 'image') {
		return <FileImage className="h-4 w-4 text-muted-foreground" />
	}
	if (['document', 'text'].includes(lowerType)) {
		return <FileText className="h-4 w-4 text-muted-foreground" />
	}
	return <File className="h-4 w-4 text-muted-foreground" />
}

export interface ArtifactLike {
	mime_type?: string | null
	type?: string | null
	extension?: string | null
}

export function getFileIconForArtifact(
	artifact: ArtifactLike | null | undefined,
	fileType?: string
): LucideIcon {
	let category: PreviewCategory = 'unsupported'
	if (artifact?.mime_type) {
		category = getArtifactPreviewCategory({
			mime_type: artifact.mime_type,
			type: artifact.type ?? '',
			extension: artifact.extension ?? null,
		})
	} else if (artifact?.extension) {
		category = getCategoryFromExtension(artifact.extension)
	} else if (artifact?.type) {
		const t = artifact.type.toLowerCase()
		if (t === 'image') category = 'image'
		else if (t === 'audio') category = 'audio'
		else if (t === 'video') category = 'video'
		else if (['document', 'text'].includes(t)) category = 'text'
	} else if (fileType) {
		category = getCategoryFromExtension(fileType)
	}
	return getIconForCategory(category)
}
